import { Routes, Route, Navigate } from "react-router-dom";
import {
  Home,
  User,
  Users,
  Roles,
  Profile,
  NotFound,
  ValidateUser,
  MaterialOffer,
  BiddingOpportunities,
  EditUser,
  JointSellerOffer,
  Chat,
  SellerOffer,
  SupplierDeliveries,
  Delivery,
  CompanyProfile,
  Offer,
  MaterialOffers,
  SupplierPurchaseOrders,
  TokenAcceptPage,
  CompanyReviewPage,
} from "../../../containers";
import {
  Opportunity,
  Layout,
  NewUserForm,
  CompanyProfileForm,
  OpportunityDashboard,
} from "../../../components";
import { EntityIdRedirect, OrderIdQueryRedirect } from "../../redirects";
import RequireAbility from "../../guards/RequireAbility";
import { ABILITIES } from "../../../utils";

// The single seller route tree (replaces the Admin / Manager / Commercial / Analyst trees). What a
// seller can reach is decided by their effective abilities (`useUser().can`), not by a per-role
// tree: each screen route is wrapped in <RequireAbility>, which redirects (never renders) when the
// ability is missing. Home, /profile, /validate-user and the legacy path normalisers stay ungated.
// The pending-admin-approval state keeps its own tree (ApprovalRoutes).
const UnifiedSellerRoutes = () => {
  return (
    <Routes>
      <Route path="/validate-user" element={<ValidateUser />} />
      {/* Public emailed-OC link: resolves for a logged-in seller too, outside the app shell. */}
      <Route
        path="/purchase-orders/token/:token"
        element={<TokenAcceptPage />}
      />
      {/* Public company-review link: the admin may already be logged in. */}
      <Route path="/company-review/:token" element={<CompanyReviewPage />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/profile" element={<Profile />} />

        <Route
          path="/dashboard"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_OPPORTUNITIES}>
              <OpportunityDashboard />
            </RequireAbility>
          }
        />
        <Route
          path="/opportunities/material/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_OPPORTUNITIES}>
              <Opportunity />
            </RequireAbility>
          }
        />

        <Route
          path="/offer/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_SUBMIT_OFFERS}>
              <SellerOffer />
            </RequireAbility>
          }
        />
        <Route
          path="/offer/multiple/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_SUBMIT_OFFERS}>
              <JointSellerOffer />
            </RequireAbility>
          }
        />
        <Route
          path="/orders/multiple/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_SUBMIT_OFFERS}>
              <Offer />
            </RequireAbility>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_SUBMIT_OFFERS}>
              <Offer />
            </RequireAbility>
          }
        />
        <Route
          path="/material/offer/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_SUBMIT_OFFERS}>
              <MaterialOffer />
            </RequireAbility>
          }
        />
        <Route
          path="/material/offers"
          element={
            <RequireAbility ability={ABILITIES.SELLER_SUBMIT_OFFERS}>
              <MaterialOffers />
            </RequireAbility>
          }
        />

        <Route
          path="/biddings"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_BIDDINGS}>
              <BiddingOpportunities />
            </RequireAbility>
          }
        />

        <Route
          path="/company"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_COMPANY}>
              <CompanyProfile />
            </RequireAbility>
          }
        />
        <Route
          path="/company/edit"
          element={
            <RequireAbility ability={ABILITIES.SELLER_MANAGE_COMPANY}>
              <CompanyProfileForm />
            </RequireAbility>
          }
        />

        <Route
          path="/deliveries"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_DELIVERIES}>
              <SupplierDeliveries />
            </RequireAbility>
          }
        />
        <Route
          path="/deliveries/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_DELIVERIES}>
              <Delivery />
            </RequireAbility>
          }
        />

        {/* The seller OC portal is gated ONLY by the seller ability — the OC module flag is the
            buyer company's, and any company can be a supplier. */}
        <Route
          path="/purchase-orders"
          element={
            <RequireAbility ability={ABILITIES.SELLER_RESPOND_PURCHASE_ORDERS}>
              <SupplierPurchaseOrders />
            </RequireAbility>
          }
        />

        <Route
          path="/users"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_USERS}>
              <Users />
            </RequireAbility>
          }
        />
        {/* A static "/users/new" always outranks the dynamic "/users/:id", so a role without
            SELLER_MANAGE_USERS is sent to the list rather than falling through to a User detail
            screen looking up a user literally called "new". */}
        <Route
          path="/users/new"
          element={
            <RequireAbility
              ability={ABILITIES.SELLER_MANAGE_USERS}
              fallback="/users"
            >
              <NewUserForm />
            </RequireAbility>
          }
        />
        <Route
          path="/users/:id/edit"
          element={
            <RequireAbility
              ability={ABILITIES.SELLER_MANAGE_USERS}
              fallback="/users"
            >
              <EditUser />
            </RequireAbility>
          }
        />
        <Route
          path="/users/:id"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_USERS}>
              <User />
            </RequireAbility>
          }
        />

        <Route
          path="/roles"
          element={
            <RequireAbility ability={ABILITIES.SELLER_MANAGE_ROLES}>
              <Roles />
            </RequireAbility>
          }
        />

        <Route
          path="/chat"
          element={
            <RequireAbility ability={ABILITIES.SELLER_VIEW_CHAT}>
              <Chat />
            </RequireAbility>
          }
        />

        {/* Legacy path normalisers — ungated on purpose: they only rewrite the path, and the
            canonical route they land on applies its own ability gate. */}
        <Route path="/order/multiple/:id" element={<OrderIdQueryRedirect />} />
        <Route
          path="/order/:id"
          element={<EntityIdRedirect to={(id) => `/orders/${id}`} />}
        />
        <Route
          path="/company/profile"
          element={<Navigate to="/company" replace />}
        />
        <Route
          path="/company/upload"
          element={<Navigate to="/company/edit" replace />}
        />
        <Route
          path="/user/:id"
          element={<EntityIdRedirect to={(id) => `/users/${id}`} />}
        />
        <Route
          path="/user/edit/:id"
          element={<EntityIdRedirect to={(id) => `/users/${id}/edit`} />}
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default UnifiedSellerRoutes;
